/**
 * Hooks React Query pour la gestion des utilisateurs
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { usersClient } from '../api';
import type {
  User,
  UserWithCapabilities,
  UserDetailWithCapabilities,
  CreateUserDto,
  UpdateUserDto,
  UpdateProfileDto,
  QueryUsersDto,
} from '@sphere/user/domain';
import type { IOffsetPaginationResponse } from '@sphere/shared/domain';

// ============================================================================
// Query Keys
// ============================================================================

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params?: QueryUsersDto) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  byIds: (ids: string[]) => [...userKeys.all, 'byIds', ids] as const,
  me: () => [...userKeys.all, 'me'] as const,
  myProfile: () => [...userKeys.all, 'myProfile'] as const,
  lookupUsername: (username: string, role: string) =>
    [...userKeys.all, 'lookup', username, role] as const,
};

// ============================================================================
// List Hooks
// ============================================================================

/**
 * Hook pour lister les utilisateurs avec pagination
 */
export function useUsers(
  params?: QueryUsersDto,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<UserWithCapabilities>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<IOffsetPaginationResponse<UserWithCapabilities>>({
    queryKey: userKeys.list(params),
    queryFn: () => usersClient.list(params),
    ...options,
  });
}

/**
 * Hook pour rechercher des utilisateurs
 */
export function useSearchUsers(
  query: string,
  params?: Omit<QueryUsersDto, 'q'>,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<UserWithCapabilities>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<IOffsetPaginationResponse<UserWithCapabilities>>({
    queryKey: userKeys.list({ ...params, q: query }),
    queryFn: () => usersClient.list({ ...params, q: query }),
    enabled: query.length > 0,
    ...options,
  });
}

/**
 * Hook pour récupérer plusieurs utilisateurs par IDs
 */
export function useUsersByIds(
  ids: string[],
  options?: Omit<
    UseQueryOptions<UserWithCapabilities[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<UserWithCapabilities[]>({
    queryKey: userKeys.byIds(ids),
    queryFn: () => usersClient.findAllByIds(ids),
    enabled: ids.length > 0,
    ...options,
  });
}

// ============================================================================
// Detail Hooks
// ============================================================================

/**
 * Hook pour récupérer un utilisateur par ID
 */
export function useUser(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<UserDetailWithCapabilities>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<UserDetailWithCapabilities>({
    queryKey: userKeys.detail(id!),
    queryFn: () => usersClient.getById(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook pour récupérer l'utilisateur connecté
 */
export function useCurrentUser(
  options?: Omit<
    UseQueryOptions<UserDetailWithCapabilities>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<UserDetailWithCapabilities>({
    queryKey: userKeys.me(),
    queryFn: () => usersClient.getMe(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook pour récupérer le profil de l'utilisateur connecté
 */
export function useMyProfile(
  options?: Omit<UseQueryOptions<User>, 'queryKey' | 'queryFn'>
) {
  return useQuery<User>({
    queryKey: userKeys.myProfile(),
    queryFn: () => usersClient.getMyProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ============================================================================
// Mutation Hooks - CRUD
// ============================================================================

/**
 * Hook pour créer un utilisateur
 */
export function useCreateUser(
  options?: UseMutationOptions<UserDetailWithCapabilities, Error, CreateUserDto>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateUserDto) => usersClient.upsert(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    ...options,
  });
}

/**
 * Hook pour mettre à jour un utilisateur
 */
export function useUpdateUser(
  options?: UseMutationOptions<
    UserDetailWithCapabilities,
    Error,
    { id: string; dto: UpdateUserDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateUserDto }) =>
      usersClient.update(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.id),
      });
    },
    ...options,
  });
}

/**
 * Hook pour supprimer un utilisateur
 */
export function useDeleteUser(
  options?: UseMutationOptions<UserDetailWithCapabilities, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersClient.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    ...options,
  });
}

// ============================================================================
// Mutation Hooks - Profile
// ============================================================================

/**
 * Hook pour mettre à jour son propre profil
 */
export function useUpdateMyProfile(
  options?: UseMutationOptions<
    UserDetailWithCapabilities,
    Error,
    UpdateProfileDto
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateProfileDto) => usersClient.updateMyProfile(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
      queryClient.invalidateQueries({ queryKey: userKeys.myProfile() });
    },
    ...options,
  });
}

/**
 * Hook pour mettre à jour le profil d'un utilisateur (admin)
 */
export function useUpdateUserProfile(
  options?: UseMutationOptions<
    UserDetailWithCapabilities,
    Error,
    { id: string; dto: UpdateProfileDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateProfileDto }) =>
      usersClient.updateProfile(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.id),
      });
    },
    ...options,
  });
}

// ============================================================================
// Mutation Hooks - Status
// ============================================================================

/**
 * Hook pour suspendre un utilisateur
 */
export function useSuspendUser(
  options?: UseMutationOptions<
    UserDetailWithCapabilities,
    Error,
    { id: string; reason?: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      usersClient.suspend(id, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.id),
      });
    },
    ...options,
  });
}

/**
 * Hook pour réactiver un utilisateur
 */
export function useUnsuspendUser(
  options?: UseMutationOptions<UserDetailWithCapabilities, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersClient.unsuspend(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
    ...options,
  });
}

/**
 * Hook pour anonymiser un utilisateur (RGPD)
 */
export function useAnonymizeUser(
  options?: UseMutationOptions<UserDetailWithCapabilities, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => usersClient.anonymize(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
    ...options,
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook pour mettre à jour le lastSeenAt (fire and forget)
 */
export function useUpdateLastSeen() {
  return useMutation({
    mutationFn: () => usersClient.updateLastSeen(),
  });
}

// ============================================================================
// Relations / Parents
// ============================================================================

/**
 * Hook pour vérifier qu'un utilisateur existe par son username
 */
export function useLookupUserByUsername(
  username: string,
  role: string,
  options?: Omit<
    UseQueryOptions<{
      id: string
      username: string | null
      displayName: string | null
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
      queryKey: userKeys.lookupUsername(username, role),
      queryFn: () => usersClient.lookupByUsername(username, role),
    enabled: username.trim().length > 0,
    retry: false,
    ...options,
  })
}

/**
 * Hook pour ajouter un parent/tuteur à un utilisateur par username
 */
export function useAddGuardian(
  options?: UseMutationOptions<
    { id: string; fromUserId: string; toUserId: string; type: string },
    Error,
    { childUserId: string; username: string }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ childUserId, username }: { childUserId: string; username: string }) =>
      usersClient.addGuardian(childUserId, username),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.childUserId),
      })
    },
    ...options,
  })
}

/**
 * Hook pour retirer un parent/tuteur d'un utilisateur
 */
export function useRemoveGuardian(
  options?: UseMutationOptions<
    { deleted: boolean },
    Error,
    { childUserId: string; guardianId: string }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      childUserId,
      guardianId,
    }: {
      childUserId: string
      guardianId: string
    }) => usersClient.removeGuardian(childUserId, guardianId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.childUserId),
      })
    },
    ...options,
  })
}

// ============================================================================
// Profile / Sync
// ============================================================================

/**
 * Hook pour synchroniser avec Keycloak
 */
export function useSyncFromKeycloak(
  options?: UseMutationOptions<
    User,
    Error,
    { email?: string; username?: string; firstName?: string; lastName?: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto) => usersClient.syncMe(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.me() });
      queryClient.invalidateQueries({ queryKey: userKeys.myProfile() });
    },
    ...options,
  });
}
